<template>
  <Card class="layout-card" style="height: 100%">
    <div class="activity-config-page">
      <div class="toolbar-row">
        <div class="search-form">
          <Select
            v-model="searchForm.activityType"
            clearable
            placeholder="活动类型"
            class="toolbar-item"
          >
            <Option
              v-for="item in activityTypeOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </Option>
          </Select>

          <Select
            v-model="searchForm.activityStatus"
            clearable
            placeholder="活动状态"
            class="toolbar-item"
          >
            <Option
              v-for="item in activityStatusOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </Option>
          </Select>

          <DatePicker
            transfer
            type="datetimerange"
            format="yyyy-MM-dd HH:mm:ss"
            :editable="false"
            :value="searchForm.activityTimeRange"
            placeholder="请选择活动时间"
            class="toolbar-item toolbar-item--time"
            @on-change="handleActivityTimeChange"
          />

          <Input
            v-model.trim="searchForm.activityName"
            clearable
            placeholder="请输入活动名称"
            class="toolbar-item toolbar-item--wide"
          />

          <Button type="primary" @click="getData(1)">搜索</Button>
          <Button @click="handleReset">重置</Button>
        </div>

        <Button type="success" icon="md-add" @click="handleOpenEditModal()">
          新增活动
        </Button>
      </div>

      <div class="table-wrap">
        <Table :data="tableData" :columns="columns" :loading="loading">
          <template #activityType="{ row }">
            <span>{{ row.activityTypeName || getActivityTypeLabel(row.activityType) }}</span>
          </template>

          <template #activityTime="{ row }">
            <span>{{ getActivityTimeText(row) }}</span>
          </template>

          <template #activityStatus="{ row }">
            <i-switch
              :value="Number(row.activityStatus)"
              :true-value="1"
              :false-value="0"
              :disabled="loading || Boolean(statusChangingId)"
              :loading="statusChangingId === row.id"
              @on-change="(value) => syncActivityStatusChange(row, value)"
            />
          </template>

          <template #action="{ row }">
            <div class="action-links">
              <a @click="handleOpenEditModal(row)">编辑</a>
              <a @click="handleOpenPreviewModal(row)">预览</a>
              <a class="link-danger" @click="handleDelete(row)">删除</a>
            </div>
          </template>

          <template #footer>
            <Pages :page="page" :func="getData" />
          </template>
        </Table>
      </div>
    </div>

    <EditModal
      v-model="editVisible"
      :data="editForm"
      :readonly="editReadonly"
      @on-success="handleSaveSuccess"
    />
  </Card>
</template>

<script>
import dayjs from 'dayjs'
import Pages from '@/components/pages'
import {
  getActivityConfigDetail,
  getActivityConfigPage,
  removeActivityConfig,
  toggleActivityConfigStatus
} from '@/api/activity'
import EditModal from './components/EditModal.vue'
import {
  ACTIVITY_STATUS_OPTIONS,
  ACTIVITY_TYPE_OPTIONS,
  ACTIVITY_TYPE_HOLIDAY,
  ACTIVITY_TYPE_SCORE,
  ACTIVITY_TYPE_WEEKEND,
  getActivityTypeLabel
} from './constants'

const createSearchForm = () => ({
  activityType: '',
  activityStatus: '',
  activityName: '',
  activityTimeRange: []
})

export default {
  name: 'ActivityConfigList',
  components: {
    Pages,
    EditModal
  },
  data() {
    return {
      activityTypeOptions: ACTIVITY_TYPE_OPTIONS,
      activityStatusOptions: ACTIVITY_STATUS_OPTIONS,
      searchForm: createSearchForm(),
      loading: false,
      statusChangingId: '',
      editVisible: false,
      editReadonly: false,
      editForm: {},
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      tableData: [],
      columns: [
        {
          title: '活动名称',
          key: 'activityName',
          minWidth: 180
        },
        {
          title: '活动类型',
          slot: 'activityType',
          minWidth: 120
        },
        {
          title: '活动时间',
          slot: 'activityTime',
          minWidth: 240
        },
        {
          title: '活动状态',
          slot: 'activityStatus',
          width: 120,
          align: 'center'
        },
        {
          title: '操作',
          slot: 'action',
          width: 220,
          align: 'center'
        }
      ]
    }
  },
  methods: {
    getActivityTypeLabel,
    normalizeTableData(records) {
      if (!Array.isArray(records)) return []

      return records.map((item) => ({
        ...item,
        activityStatus:
          item.activityStatus === '' ||
          item.activityStatus === null ||
          item.activityStatus === undefined
            ? item.activityStatus
            : Number(item.activityStatus)
      }))
    },
    buildSearchParams() {
      const params = {
        current: this.page.current,
        size: this.page.size,
        activityName: this.searchForm.activityName || undefined,
        activityType:
          this.searchForm.activityType === 0 || this.searchForm.activityType
            ? Number(this.searchForm.activityType)
            : undefined,
        activityStatus:
          this.searchForm.activityStatus === 0 || this.searchForm.activityStatus
            ? Number(this.searchForm.activityStatus)
            : undefined
      }

      if (
        Array.isArray(this.searchForm.activityTimeRange) &&
        this.searchForm.activityTimeRange.length === 2
      ) {
        params.activityStartTime = this.searchForm.activityTimeRange[0]
        params.activityEndTime = this.searchForm.activityTimeRange[1]
      }

      return params
    },
    getActivityTimeText(row) {
      if (row.activityTimeDesc) return row.activityTimeDesc

      const activityType = Number(row.activityType)
      if (
        activityType === ACTIVITY_TYPE_HOLIDAY &&
        row.startTime &&
        row.endTime
      ) {
        return (
          dayjs(row.startTime).format('YYYY-MM-DD HH:mm:ss') +
          ' - ' +
          dayjs(row.endTime).format('YYYY-MM-DD HH:mm:ss')
        )
      }

      if (activityType === ACTIVITY_TYPE_WEEKEND) return '每周固定时间'
      if (activityType === ACTIVITY_TYPE_SCORE) return '长期有效'
      return '-'
    },
    handleActivityTimeChange(value) {
      this.searchForm.activityTimeRange = Array.isArray(value) ? value : []
    },
    handleReset() {
      this.searchForm = createSearchForm()
      this.getData(1)
    },
    async getData(current) {
      if (current) this.page.current = current

      this.loading = true
      try {
        const { data } = await getActivityConfigPage(this.buildSearchParams())
        this.tableData = this.normalizeTableData(data.records)
        this.page.total = data.total || 0
        this.page.current = data.current || this.page.current
      } catch (error) {
        this.$Message.error('获取活动配置列表失败')
      } finally {
        this.loading = false
      }
    },
    async openDetailModal(row, readonly = false) {
      if (!row || !row.id) {
        this.editForm = {}
        this.editReadonly = false
        this.editVisible = true
        return
      }

      this.loading = true
      try {
        const { data } = await getActivityConfigDetail(row.id)
        this.editForm = data || {}
        this.editReadonly = readonly
        this.editVisible = true
      } catch (error) {
        this.$Message.error('获取活动配置详情失败')
      } finally {
        this.loading = false
      }
    },
    handleOpenEditModal(row) {
      this.openDetailModal(row, false)
    },
    handleOpenPreviewModal(row) {
      this.openDetailModal(row, true)
    },
    handleSaveSuccess() {
      this.getData(this.page.current)
    },
    async syncActivityStatusChange(row, value) {
      if (this.loading || this.statusChangingId) return

      const previousStatus = Number(row.activityStatus)
      const nextStatus = Number(value)

      if (previousStatus === nextStatus) return

      try {
        this.statusChangingId = row.id
        this.$set(row, 'activityStatus', nextStatus)
        await toggleActivityConfigStatus(row.id)
        this.getData(this.page.current)
        this.$Message.success('状态切换成功')
      } catch (error) {
        this.$set(row, 'activityStatus', previousStatus)
        this.$Message.error('状态切换失败')
      } finally {
        this.statusChangingId = ''
      }
    },
    handleDelete(row) {
      this.$Modal.confirm({
        title: '提示',
        content: `是否确认删除活动 "${row.activityName}"？`,
        onOk: async () => {
          try {
            await removeActivityConfig(row.id)
            this.$Message.success('删除成功')
            const shouldGoPrevPage =
              this.tableData.length === 1 && this.page.current > 1
            this.getData(
              shouldGoPrevPage ? this.page.current - 1 : this.page.current
            )
          } catch (error) {
            this.$Message.error('删除失败')
          }
        }
      })
    }
  },
  mounted() {
    this.getData()
  }
}
</script>

<style scoped lang="less">
.layout-card {
  /deep/ .ivu-card-body {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.activity-config-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.toolbar-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.search-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.toolbar-item {
  width: 150px;
}

.toolbar-item--wide {
  width: 220px;
}

.toolbar-item--time {
  width: 320px;
}

.table-wrap {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.action-links {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.link-danger {
  color: #ed4014;
}
</style>
